import { PaymentRequest, SendDepositParametersState, SuccessResponse } from '~/components/payment/deposit/data/types';
import { requestApi } from '~/services/api';

declare global {
	interface Window {
		ApplePaySession: typeof ApplePaySession;
	}
}

const AMEX = 'amex';
const APPLE_PAY_VERSION = 2;
const DISCOVER = 'discover';
const MASTERCARD = 'masterCard';
const SUPPORTS3DS = 'supports3DS';
const VISA = 'visa';

export const hasApplePaySupport = async (): Promise<boolean> => {
	if (!window.ApplePaySession) return false;
	/*
		The need for await depends on the version of Apple Pay JS API we are using.
		*/

	const supportVersion: boolean = await ApplePaySession.supportsVersion(APPLE_PAY_VERSION);

	const canMakePayments: boolean = await ApplePaySession.canMakePayments();

	return supportVersion && canMakePayments;
};

export const createPaymentRequest = (state: SendDepositParametersState, countryId: number): PaymentRequest => {
	const { amount, cu } = state.sendDepositParameters;

	const countryCodeMap: { [key: number]: string } = {
		1: 'SE',
		2: 'GB',
		83: 'DE',
		62: 'DK',
		42: 'CA',
		76: 'FI'
	};

	return {
		countryCode: countryCodeMap[countryId],
		currencyCode: cu,
		total: {
			label: 'Apple Pay deposit',
			amount: String(amount)
		},
		supportedNetworks: [AMEX, DISCOVER, MASTERCARD, VISA],
		merchantCapabilities: [SUPPORTS3DS]
	};
};

const handleSuccessResponse = (response: SuccessResponse) => {
	/*
	This will navigate to:
	 https://mobile.comeon.cleverdolphin.se/payment/result/deposit?accHistRef=PR-2245249753728-702&fetch_type=fetch_type_asynch
	 window.location.href = '/payment/result/deposit?accHistRef=PR-2245249753728-702&fetch_type=fetch_type_asynch';
	 */
	const { accHistRef } = response;
	window.location.href = `/payment/result/deposit?accHistRef=${accHistRef}&fetch_type=fetch_type_asynch`;
};

export const setUpApplePaySession = (
	paymentRequest: ApplePayJS.ApplePayPaymentRequest,
	url: string,
	amount: number, // total amount without fees
	currency: string,
	bonusId?: string
) => {
	const session = new window.ApplePaySession(APPLE_PAY_VERSION, paymentRequest);

	/**
	 * Merchant Validation
	 */
	session.onvalidatemerchant = ({ validationURL }) => {
		requestApi(url.concat('/getApplePaySession'), { validationURL, amount, currency, bonusId }, 'POST')
			.then(response => {
				session.completeMerchantValidation(response);
			})
			.catch(error => {
				// eslint-disable-next-line
				console.error('error while getting apple pay session', error);

				session.abort();
			});
	};

	/**
	 * Payment Authorization
	 * we receive the encrypted payment data and then send it
	 * on to payment provider for processing, and return an appropriate
	 * response
	 */
	session.onpaymentauthorized = event => {
		const paymentToken = JSON.stringify(event.payment.token);

		requestApi(url.concat('/authorise'), { paymentToken }, 'POST')
			.then(response => {
				// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
				if (response.successful) {
					session.completePayment(window.ApplePaySession.STATUS_SUCCESS);

					handleSuccessResponse(response);
				} else {
					session.completePayment(window.ApplePaySession.STATUS_FAILURE);
				}
			})
			.catch(error => {
				// eslint-disable-next-line
				console.error('apple-pay:error while authorising apple pay session', error);
			});
	};

	// All our handlers are setup
	return session;
};

export const applePaySupportedMethodCodes: string[] = ['APPLE_PAY', 'SHIFT4_APPLEPAY', 'LUQAPAY_NETBANKING'];

export const isApplePaySupported = async (playerCurrency: string): Promise<boolean> => {
	try {
		const support = await hasApplePaySupport();

		return !!(
			support &&
			(playerCurrency === 'SEK' ||
				playerCurrency === 'EUR' ||
				playerCurrency === 'DKK' ||
				playerCurrency === 'CAD' ||
				playerCurrency === 'NOK')
		);
	} catch (err) {
		// eslint-disable-next-line no-console
		console.error('Applepay error: ', err);
	}

	return false;
};
